import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { api, CUSTOM_HOST } from "./lib/api";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, Protected } from "./lib/auth";
import { Landing, TemplatesPage, FeaturesPage, PricingPage, LegalPage, LoginPage, TemplatePreview, ContactPage, DomainHelpPage } from "./pages/Marketing";
import AppShell from "./app/AppShell";
import Onboarding from "./app/Onboarding";
import { Dashboard, Analytics } from "./app/Dashboard";
import { Products, ProductEditor } from "./app/Products";
import { Collections, Inventory, Discounts } from "./app/Catalog";
import { Orders, OrderDetail } from "./app/Orders";
import { Customers, Returns } from "./app/People";
import { Website, Themes, Domains, Stores } from "./app/Website";
import { MarketingAds } from "./app/MarketingAds";
import Builder from "./app/Builder";
import { Payments, Shipping } from "./app/Integrations";
import { Business, Taxes, Policies, Notifications, Team, Subscription, Invoices, Security } from "./app/Settings";
import Admin from "./admin/Admin";
import Storefront from "./storefront/Storefront";

function Router() {
  const appPages = { dashboard: Dashboard, stores: Stores, products: Products, collections: Collections, inventory: Inventory, orders: Orders, customers: Customers, returns: Returns,
    discounts: Discounts, analytics: Analytics, website: Website, themes: Themes, payments: Payments, shipping: Shipping, finance: Invoices, invoices: Invoices, domains: Domains, marketing: MarketingAds };
  const settings = { business: Business, taxes: Taxes, policies: Policies, notifications: Notifications, team: Team, subscription: Subscription, security: Security };
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/templates" element={<TemplatesPage />} />
      <Route path="/templates/:code" element={<TemplatePreview />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/integrations" element={<FeaturesPage integrations />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/help/domains" element={<DomainHelpPage />} />
      <Route path="/terms" element={<LegalPage kind="terms" />} />
      <Route path="/privacy" element={<LegalPage kind="privacy" />} />
      <Route path="/legal" element={<LegalPage kind="legal" />} />
      <Route path="/refund-policy" element={<LegalPage kind="refund" />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<LoginPage signup />} />
      <Route path="/forgot-password" element={<LoginPage forgot />} />
      <Route path="/reset-password" element={<LoginPage reset />} />
      <Route path="/s/:slug/*" element={<Storefront />} />
      <Route path="/app/onboarding" element={<Protected><Onboarding /></Protected>} />
      <Route path="/app/website/builder" element={<Protected><Builder /></Protected>} />
      <Route path="/app" element={<Protected><AppShell /></Protected>}>
        <Route index element={<Navigate to="dashboard" replace />} />
        {Object.entries(appPages).map(([k, C]) => <Route key={k} path={k} element={<C />} />)}
        <Route path="products/:id" element={<ProductEditor />} />
        <Route path="orders/:id" element={<OrderDetail />} />
        {Object.entries(settings).map(([k, C]) => <Route key={k} path={`settings/${k}`} element={<C />} />)}
      </Route>
      <Route path="/super-admin/*" element={<Protected role="super_admin"><Admin /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function HostStore() {
  const [slug, setSlug] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/storefront-host", { params: { host: CUSTOM_HOST } }).then((r) => setSlug(r.data.slug)).catch(() => setErr("No store is connected to this address yet.")); }, []);
  if (err) return <div className="min-h-screen grid place-items-center text-center p-6" data-testid="host-store-missing"><div><div className="text-2xl font-bold mb-2">Store not found</div><p className="text-slate-500">{err}</p></div></div>;
  if (!slug) return <div className="min-h-screen" />;
  return <Routes><Route path="/*" element={<Storefront slug={slug} base="" />} /></Routes>;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        {CUSTOM_HOST ? <HostStore /> : <Router />}
        <Toaster richColors position="bottom-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}
